/**
 * Conformity-algebra types for the portal's read-only predicate visualization.
 *
 * The grammar (`Expr`) and result types (`ConformityResult`, `Evaluated`, …) are
 * CENTRAL in @the-real-insight/tri-model (SPEC-conformity-algebra) and are
 * imported by tri-server (engine) + tri-data-pipelines (compiler). The portal
 * does NOT redefine them — it re-exports them here and adds only the portal-only
 * obligation envelope, the unit resolver, and Expr type guards.
 *
 *   Expr = Atom | Not | And | Or | Quant | Count | Const   (kind-discriminated)
 *   part-reads are Quant{exists|forall, typeCode, partRole?, body}, body atoms read source:"asset".
 *
 * Mode B consumes the engine's `ConformityResult` (status + per-stage `Evaluated`
 * {value, leaves[]} + `decisive` leaves + `missing`); the portal never evaluates.
 */
import type {
  AtomExpr,
  QuantExpr,
  CountExpr,
  Expr,
  ReviewStatus,
  Conformity,
} from "@the-real-insight/tri-model";

export type {
  Tri,
  Source,
  Cmp,
  CountCmp,
  AttrValue,
  AtomExpr,
  NotExpr,
  AndExpr,
  OrExpr,
  QuantExpr,
  CountExpr,
  ConstExpr,
  Expr,
  Where,
  ReasonKind,
  Missing,
  Leaf,
  Evaluated,
  ReviewStatus,
  Conformity,
  ConformityStatus,
  ConformityResult,
} from "@the-real-insight/tri-model";

/** Expr type guards over the spec's `kind` discriminator. */
export const isAtom = (e: Expr | null | undefined): e is AtomExpr => !!e && e.kind === "atom";
export const isQuant = (e: Expr | null | undefined): e is QuantExpr =>
  !!e && (e.kind === "exists" || e.kind === "forall");
export const isCount = (e: Expr | null | undefined): e is CountExpr => !!e && e.kind === "count";
export const isBool = (e: Expr | null | undefined): boolean =>
  !!e && (e.kind === "and" || e.kind === "or" || e.kind === "not");

/** The conformity obligation envelope the review/asset panels pass in (Mode A). */
export interface ConformityObligation {
  obligationKey: string;
  version?: number;
  regulationId?: string;
  provisionId?: string;
  regulatedObjectCode?: string | null;
  modality?: "must" | "should" | "mustNot";
  reviewStatus?: ReviewStatus;
  /** Norm source string (e.g. "DIN 18065") for the norm chip / hEN badge. */
  source?: string;
  /** The compiled sub-doc; `sourceText` is the portal-only human-readable norm text. */
  conformity?: Conformity & { sourceText?: string };
}

/** Resolve a canonical attribute's unit (from the DIN-276 ontology), or null.
 *  Part attributes carry a `typeCode` (the enclosing Quant); asset/building → undefined. */
export type UnitResolver = (typeCode: string | undefined, attr: string) => string | null;
